import React, { useState } from "react";
import { connect } from "react-redux";
import {
    NikeDesignSystemProvider,
    TextFieldStyled,
    TextAreaStyled,
} from "@nike/nike-design-system-components";
import {Col, Form, Row, Modal} from "react-bootstrap";
import {Button, PageSpinner} from "@nike/epic-react-ui";
import {RetryFFRService} from "../../services/service.ffrRetry";
import {toast} from "react-toastify";
import config from "../../config/config";
import {
    isAddressLine1TextEditable,
    isAddressLine2TextEditable,
    isAddressLine3TextEditable,
    isCityNameEditable, isPostalCodeEditable,
    isStateProvinceCodeEditable
} from "../../config/addressDcPermissions";

// Shared limit for both bulk Retry and bulk Cancel actions.
const MAX_BULK_FFR_COUNT = config.maxBulkFfrCount || 10;

const FFR_REGEX = /^[A-Z0-9]{1,50}$/;

const parseFfrNumbers = (input) => {
    const rawFfrNumbers = input
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
    // Remove duplicate FFR numbers (preserving order) so that count-based validations
    // (e.g. the max-10 limit) and the payload sent to the backend aren't inflated by
    // the user accidentally entering the same FFR number more than once.
    return [...new Set(rawFfrNumbers)];
};

// Renders a categorised result summary (used for both Retry and Cancel results).
function FfrResultPanel({ title, result, categories }) {
    if (!result) {
        return null;
    }
    const statusColor = result.status === "success" ? "green"
        : result.status === "partial_success" ? "orange"
        : "red";
    return (
        <div className="mt-3 p-3 border rounded" style={{ background: "#fff" }}>
            <h6>{title}: <span style={{ color: statusColor }}>{result.status?.toUpperCase()}</span></h6>
            <p style={{ fontSize: "0.9rem" }}>{result.message}</p>
            {categories.map(({ key, label, color, note }) => {
                const list = result[key];
                if (!list || list.length === 0) {
                    return null;
                }
                return (
                    <div className="mb-2" key={key}>
                        <strong style={{ color }}>{label} ({list.length}):</strong>
                        <div style={{ fontSize: "0.85rem", marginLeft: "1rem", color: note ? color : undefined }}>
                            {list.join(", ")}
                            {note && (<><br /><em>{note}</em></>)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function FfrRetry(props) {

    const [showSpinner , setShowSpinner] = useState(false);
    // Single shared input for Retry / View Address / Cancel - comma separated list of FFR numbers.
    const [ffrInput, setFfrInput] = useState("");
    const [viewedFfrNumber, setViewedFfrNumber] = useState("");
    const [showAddressFields, setShowAddressFields] = useState(false);
    const [showConfirmCancel, setShowConfirmCancel] = useState(false);
    const [cancelResult, setCancelResult] = useState(null);
    const [retryResult, setRetryResult] = useState(null);
    const [address, setAddress] = useState({
        addressLine1Text: "",
        addressLine2Text: "",
        addressLine3Text: "",
        cityName: "",
        stateProvinceCode: "",
        postalCode: "",
        lastUpdateDateTime: "",
        lastUpdatedUser: "",
    });

    const ffrNumbers = parseFfrNumbers(ffrInput);
    const ffrCount = ffrNumbers.length;
    // View Address is strictly single-FFR: exactly one entry, and it must be a valid FFR format.
    const isViewAddressEligible = ffrCount === 1 && FFR_REGEX.test(ffrNumbers[0]);

    function handleRefresh() {
        setFfrInput("");
        clearActionPanels();
    }

    // Clears every action's result/detail panel (Retry result, Cancel result, View Address
    // block) without touching the FFR input box. Call this at the start of any action so that
    // only the panel for the action just performed is shown — results never stack/combine.
    function clearActionPanels() {
        setViewedFfrNumber("");
        setShowAddressFields(false);
        setCancelResult(null);
        setRetryResult(null);
        setAddress({
            addressLine1Text: "",
            addressLine2Text: "",
            addressLine3Text: "",
            cityName: "",
            stateProvinceCode: "",
            postalCode: "",
            lastUpdateDateTime: "",
            lastUpdatedUser: "",
        });
    }

    const retryFfr = async () => {
        if (ffrCount === 0) {
            toast.error("Please enter at least one FFR Number to retry.");
            return;
        }
        const invalid = ffrNumbers.filter((f) => !FFR_REGEX.test(f));
        if (invalid.length > 0) {
            toast.error(<>Invalid FFR Number(s): <b>{invalid.join(", ")}</b>. FFR Numbers must be alphanumeric.</>);
            return;
        }
        if (ffrCount > MAX_BULK_FFR_COUNT) {
            toast.error(<>Cannot retry more than <b>{MAX_BULK_FFR_COUNT}</b> FFRs at once. You entered {ffrCount}.</>);
            return;
        }
        setShowSpinner(true);
        // Only the Retry panel should be visible for this action — clear any stale
        // View Address / Cancel panel left over from a previous action.
        clearActionPanels();
        try {
            const response = await RetryFFRService.retryFfrNumber(ffrNumbers);
            setShowSpinner(false);
            const data = response?.data;
            if (data) {
                setRetryResult(data);
                if (data.status === "success") {
                    toast.success("All FFRs retried successfully.");
                } else if (data.status === "partial_success") {
                    toast.info("Partial retry — see results below.");
                } else {
                    toast.warn(data.message || "Retry was not successful.");
                }
            }
        } catch (err) {
            setShowSpinner(false);
            const errData = err?.data || err?.response?.data;
            if (errData && errData.message) {
                toast.error(errData.message);
            } else {
                toast.error("An error occurred while retrying FFRs.");
            }
        }
    };

    const handleCancelClick = () => {
        if (ffrCount === 0) {
            toast.error("Please enter at least one FFR Number to cancel.");
            return;
        }
        const invalid = ffrNumbers.filter((f) => !FFR_REGEX.test(f));
        if (invalid.length > 0) {
            toast.error(<>Invalid FFR Number(s): <b>{invalid.join(", ")}</b>. FFR Numbers must be alphanumeric.</>);
            return;
        }
        if (ffrCount > MAX_BULK_FFR_COUNT) {
            toast.error(<>Cannot cancel more than <b>{MAX_BULK_FFR_COUNT}</b> FFRs at once. You entered {ffrCount}.</>);
            return;
        }
        setShowConfirmCancel(true);
    };

    const confirmCancel = async () => {
        setShowConfirmCancel(false);
        setShowSpinner(true);
        // Only the Cancel panel should be visible for this action — clear any stale
        // View Address / Retry panel left over from a previous action.
        clearActionPanels();
        try {
            const response = await RetryFFRService.cancelFfr(ffrNumbers);
            setShowSpinner(false);
            const data = response?.data;
            if (data) {
                setCancelResult(data);
                if (data.status === "success") {
                    toast.success("All FFRs cancelled successfully.");
                } else if (data.status === "partial_success") {
                    toast.info("Partial cancellation — see results below.");
                } else {
                    toast.warn(data.message || "Cancellation was not successful.");
                }
            }
        } catch (err) {
            setShowSpinner(false);
            const errData = err?.data || err?.response?.data;
            if (errData && errData.message) {
                toast.error(errData.message);
            } else {
                toast.error("An error occurred while cancelling FFRs.");
            }
        }
    };

    const getAddress = async () => {
        // View Address is strictly single-FFR. The button is disabled unless exactly one
        // valid FFR is entered, but keep this guard as a defensive fallback too.
        if (ffrCount !== 1) {
            toast.error(
                <>
                    View Address supports only <b>one</b> FFR Number at a time.
                    You entered <b>{ffrCount}</b> FFR Number{ffrCount === 1 ? "" : "s"}
                    {ffrCount > 0 ? <>: <b>{ffrNumbers.join(", ")}</b></> : null}.
                    Please enter exactly one FFR Number.
                </>
            );
            return;
        }
        const singleFfr = ffrNumbers[0];
        if (!FFR_REGEX.test(singleFfr)) {
            toast.error(<>Invalid format for FFR Number: <b>{singleFfr}</b> FFR Number must be numeric, Please enter a valid FFR Number</>);
            return;
        }
        setShowSpinner(true);
        // Only the View Address panel should be visible for this action — clear any stale
        // Retry / Cancel result panel left over from a previous action.
        clearActionPanels();
        await RetryFFRService.getAddress(singleFfr, address)
            .then((response) => {
                setShowSpinner(false);
                const { originalAddress, lastUpdateDateTime, lastUpdatedUser } = response?.data || {};
                if (originalAddress) {
                    setViewedFfrNumber(singleFfr);
                    setAddress({
                        ...originalAddress,
                        lastUpdateDateTime: lastUpdateDateTime || "",
                        lastUpdatedUser: lastUpdatedUser || ""
                    });
                    toast.success(<>Address fetched for FFR Number: <b>{singleFfr}</b></>);
                    setShowAddressFields(true);
                    // Reset the input box so it's empty and ready to accept a new FFR number,
                    // now that the address details for this FFR are displayed below.
                    setFfrInput("");
                }
                else {
                    toast.error(<>No address data found for FFR Number: <b>{singleFfr}</b></>);
                }
            })
            .catch((response) => {
                setShowSpinner(false);
                if (response !== undefined && response.data !== undefined) {
                    handleRefresh();
                    toast.error(<>No fulfillment events found for the provided FFR number: <b>{singleFfr}</b> Please enter a valid FFR Number</>);
                }
            });
    }

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setAddress((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleUpdateAndRetry = async () => {
        const payload = {
            ffrNumber: viewedFfrNumber,
            shipToAddress: {
                addressLine1Text: address.addressLine1Text,
                addressLine2Text: address.addressLine2Text,
                addressLine3Text: address.addressLine3Text,
                cityName: address.cityName,
                stateProvinceCode: address.stateProvinceCode,
                postalCode: address.postalCode,
            },
            lastUpdatedBy: props.loggedInUser,
        };
        setShowSpinner(true);
        await RetryFFRService.updateRetryFfrAddress(payload)
            .then((response) => {
                setShowSpinner(false);
                if (response && response.data) {
                    toast.success("Address updated and retry triggered!");
                }
                handleRefresh();
            })
            .catch((response) => {
                setShowSpinner(false);
                if (response && response.status === 400 && response.data && response.data.message) {
                    toast.error(<>Bad Request: {response.data.message}</>);
                } else if (
                    response && response.status === 409 && response.data && response.data.message
                ) {
                    toast.error(<>{response.data.message}</>);
                } else if (response && response.data !== undefined) {
                    toast.error(<>No fulfillment events found for the provided FFR number: <b>{viewedFfrNumber}</b> Please
                        enter a valid FFR Number</>);
                }
                handleRefresh();
            });
    };

    return (
        <div style={{ paddingBottom: "4rem" }}>
            <PageSpinner show={showSpinner} />
            <NikeDesignSystemProvider>
                {/* Shared FFR input for Retry / View Address / Cancel */}
                <Row className="py-2 mt-2">
                    <Form.Group as={Col}>
                        <Form.Label className={"fw-bold"}>Fulfillment Request Number(s)</Form.Label>
                        <TextAreaStyled
                            id="ffr-input"
                            test-id="ffr-input-textarea"
                           // label="FFR / DN Number(s)"
                            placeholder="FFR / DN Number(s)"
                            value={ffrInput}
                            onChange={(e) => setFfrInput(e.target.value)}
                            resize="vertical"
                            rows={3}
                            style={{
                                wordWrap: "break-word",
                                overflowWrap: "break-word",
                                whiteSpace: "pre-wrap",
                                overflowX: "hidden",
                                overflowY: "auto",
                                maxHeight: "220px",
                                width: "100%",
                            }}
                        />
                        <div style={{ fontSize: "0.8rem", color: "#707072", marginTop: "4px" }}>
                            Enter FFR numbers as a comma-separated list (e.g. <b>FFR001, FFR002, FFR003</b>).
                        </div>
                        <div style={{ fontSize: "0.85rem", color: "#707072", marginTop: "4px" }}>
                            {ffrCount} FFR{ffrCount === 1 ? "" : "s"} entered
                        </div>
                    </Form.Group>
                </Row>

                <div className="d-flex align-items-start justify-content-center flex-wrap mt-2"
                     style={{ gap: "3rem", width: "100%" }}>
                    <div className="text-center">
                        <Button
                            type="NikeApp"
                            iconsize="m"
                            test-id="reset-button"
                            className="text-center"
                            onClick={() => handleRefresh()}
                            disabled={!ffrInput && !showAddressFields && !retryResult && !cancelResult}
                        >
                            Reset
                        </Button>
                    </div>

                    <div className="text-center">
                        <Button
                            iconsize="m"
                            test-id="retry-button"
                            className="text-center"
                            onClick={() => retryFfr()}
                            disabled={ffrCount === 0 || ffrCount > MAX_BULK_FFR_COUNT}
                        >
                            Retry
                        </Button>
                        <div style={{ fontSize: "0.75rem", color: "#707072", marginTop: "4px", maxWidth: "160px" }}>
                            Max <b>{MAX_BULK_FFR_COUNT}</b> FFRs per retry.
                        </div>
                    </div>

                    <div className="text-center">
                        <Button
                            iconsize="m"
                            test-id="update-address-button"
                            className="text-center"
                            onClick={() => getAddress()}
                            disabled={!isViewAddressEligible}
                        >
                            View Address
                        </Button>
                        <div style={{ fontSize: "0.75rem", color: "#707072", marginTop: "4px", maxWidth: "160px" }}>
                            Exactly <b>1</b> valid FFR required.
                        </div>
                    </div>

                    <div className="text-center">
                        <Button
                            type="NikeApp"
                            iconsize="m"
                            test-id="cancel-button"
                            className="text-center"
                            onClick={handleCancelClick}
                            disabled={ffrCount === 0 || ffrCount > MAX_BULK_FFR_COUNT}
                        >
                            Cancel FFRs
                        </Button>
                        <div style={{ fontSize: "0.75rem", color: "#707072", marginTop: "4px", maxWidth: "180px" }}>
                            Max <b>{MAX_BULK_FFR_COUNT}</b> FFRs. Only <b>Failed</b> and <b>CD_Failed</b> FFRs can be cancelled.
                        </div>
                    </div>
                </div>

                {/* Cancel Confirmation Modal */}
                <Modal show={showConfirmCancel} onHide={() => setShowConfirmCancel(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Cancellation</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>Are you sure you want to proceed?</p>
                        <p>You are about to cancel <b>{ffrCount}</b> FFR(s).</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button type="NikeApp" onClick={() => setShowConfirmCancel(false)}>No, Go Back</Button>
                        <Button onClick={confirmCancel}>Yes, Cancel FFRs</Button>
                    </Modal.Footer>
                </Modal>

                {/* Retry Result Summary */}
                <FfrResultPanel
                    title="Retry Result"
                    result={retryResult}
                    categories={[
                        { key: "processedFFRNumbers", label: "Retried", color: "green" },
                        { key: "notFoundFFRNumbers", label: "Not Found", color: "#b8860b" },
                    ]}
                />

                {/* Cancel Result Summary */}
                <FfrResultPanel
                    title="Cancellation Result"
                    result={cancelResult}
                    categories={[
                        { key: "processedFFRNumbers", label: "Cancelled", color: "green" },
                        { key: "alreadyCancelledFFRNumbers", label: "Already Cancelled", color: "#888" },
                        {
                            key: "notEligibleFFRNumbers", label: "Not Eligible", color: "red",
                            note: "Only Failed and CD_Failed FFRs can be cancelled. FFRs in CD_Validation_Failed or Validation_Failed state cannot be cancelled."
                        },
                        { key: "notFoundFFRNumbers", label: "Not Found", color: "#b8860b" },
                    ]}
                />

                {showAddressFields && (
                    <div className="mt-4 p-2 border rounded w-100" style={{maxWidth: "100vw"}}>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <div className="fw-bold" style={{ fontSize: "0.9rem" }}>
                                    Fulfillment Request Number: {viewedFfrNumber}
                                </div>
                                <div style={{ fontSize: "0.8rem", color: "#666" }}>
                                    Destination Address Only<span style={{ color: "red" }}>*</span>
                                </div>
                            </div>
                            <div className="text-end ms-3" style={{ minWidth: 200, fontSize: "0.9rem"}}>
                                <div className="mb-2">
                                    <strong>Last updated timestamp:</strong> {address.lastUpdateDateTime}
                                </div>
                                <div>
                                    <strong>Last updated user:</strong> {address.lastUpdatedUser}
                                </div>
                            </div>
                        </div>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>Address Line 1</Form.Label>
                                    <TextFieldStyled
                                        value={address.addressLine1Text}
                                        onChange={handleAddressChange}
                                        name="addressLine1Text"
                                        placeholder="Address Line 1"
                                        label="Address Line 1"
                                        readOnly={!isAddressLine1TextEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>Address Line 2</Form.Label>
                                    <TextFieldStyled
                                        value={address.addressLine2Text}
                                        onChange={handleAddressChange}
                                        name="addressLine2Text"
                                        placeholder="Address Line 2"
                                        label="Address Line 2"
                                        readOnly={!isAddressLine2TextEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>Address Line 3</Form.Label>
                                    <TextFieldStyled
                                        value={address.addressLine3Text}
                                        onChange={handleAddressChange}
                                        name="addressLine3Text"
                                        placeholder="Address Line 3"
                                        label="Address Line 3"
                                        readOnly={!isAddressLine3TextEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>City</Form.Label>
                                    <TextFieldStyled
                                        value={address.cityName}
                                        onChange={handleAddressChange}
                                        name="cityName"
                                        placeholder="City"
                                        label="City"
                                        readOnly={!isCityNameEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>State</Form.Label>
                                    <TextFieldStyled
                                        value={address.stateProvinceCode}
                                        onChange={handleAddressChange}
                                        name="stateProvinceCode"
                                        placeholder="State"
                                        label="State"
                                        readOnly={!isStateProvinceCodeEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-1">
                                    <Form.Label>Postal Code</Form.Label>
                                    <TextFieldStyled
                                        value={address.postalCode}
                                        onChange={handleAddressChange}
                                        name="postalCode"
                                        placeholder="Postal Code"
                                        label="Postal Code"
                                        readOnly={!isPostalCodeEditable(props.dcValue)}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="justify-content-center">
                            <Col md={3}></Col>
                            <Col md={6} className="d-flex justify-content-center">
                                <Button
                                    iconsize="m"
                                    test-id="update-and-retry-button"
                                    onClick={handleUpdateAndRetry}
                                >
                                    Update and Retry
                                </Button>
                            </Col>
                            <Col md={3}></Col>
                        </Row>
                    </div>
                )}
            </NikeDesignSystemProvider>
        </div>
    );
}

export default connect()(FfrRetry);

export { FfrRetry };
